import React, {createContext, ReactNode, useContext} from "react";
import {Text, View} from "react-native";
import {NativeStackNavigationProp} from "@react-navigation/native-stack";
import MainStyle from "../../../MainStyle";

const promotionsStyles = {
  text_l: [MainStyle.c_white, MainStyle.Barlow400, MainStyle.f_s_24, MainStyle.m_b_30, MainStyle.l_h_36],
  text_s: [MainStyle.c_white, MainStyle.Barlow400, MainStyle.f_s_12, MainStyle.m_b_15, MainStyle.l_h_16],

  link_l: [MainStyle.c_lightblue, MainStyle.Barlow700, MainStyle.f_s_24, MainStyle.t_d_l_u, MainStyle.l_h_36],
  link_s: [MainStyle.c_lightblue, MainStyle.Barlow700, MainStyle.f_s_12, MainStyle.t_d_l_u, MainStyle.l_h_16],

  small_link: [MainStyle.c_lightblue, MainStyle.Barlow700, MainStyle.t_d_l_u],

  secondary_l: [MainStyle.c_white, MainStyle.Barlow400, MainStyle.f_s_18, MainStyle.m_b_30, MainStyle.l_h_36],
  secondary_s: [MainStyle.c_white, MainStyle.Barlow400, MainStyle.f_s_10, MainStyle.m_b_15, MainStyle.l_h_16],

  title_l: [MainStyle.c_lightblue, MainStyle.Barlow700, MainStyle.f_s_32, MainStyle.m_b_30, MainStyle.t_t_u],
  title_s: [MainStyle.c_lightblue, MainStyle.Barlow700, MainStyle.f_s_16, MainStyle.m_b_15, MainStyle.t_t_u],

  dot_l: [MainStyle.c_white, MainStyle.Barlow400, MainStyle.f_s_24, MainStyle.l_h_36, MainStyle.m_x_16],
  dot_s: [MainStyle.c_white, MainStyle.Barlow400, MainStyle.f_s_12, MainStyle.l_h_16, MainStyle.m_x_8]
};

type PromotionsTextContextValue = {
  isSm: boolean, styles: typeof promotionsStyles, navigation: NativeStackNavigationProp<any>,
  openLink: (url: string) => void;
};

const PromotionsTextContext = createContext<PromotionsTextContextValue | null>(null);

export type ScrollTargetOptions = {
  behavior?: 'auto' | 'instant' | 'smooth', block?: 'center' | 'end' | 'nearest' | 'start',
  inline?: 'center' | 'end' | 'nearest' | 'start'
};

export type ScrollTarget = { scrollIntoView?: (options?: ScrollTargetOptions) => void };

export type SectionHeadingRef = (node: unknown) => void;

export const PromotionsTextProvider = (props: Omit<PromotionsTextContextValue, 'styles'> & { children: ReactNode }) => {
  const {isSm, navigation, openLink, children} = props;

  return (
    <PromotionsTextContext.Provider value={{isSm, styles: promotionsStyles, navigation, openLink}}>
      {children}
    </PromotionsTextContext.Provider>
  );
};

export const usePromotionsText = () => {
  const context = useContext(PromotionsTextContext);

  if (!context) {
    throw new Error('Promotions text components must be rendered inside PromotionsTextProvider.');
  }

  return context;
};

type ChildrenProps = { children: ReactNode };

export const Bold = ({children}: ChildrenProps) => (
  <Text style={MainStyle.Barlow700}>{children}</Text>
);

export const InlineLink = ({children, onPress}: ChildrenProps & { onPress: () => void }) => {
  const {styles} = usePromotionsText();

  return <Text style={styles.small_link} onPress={onPress} accessibilityRole="link">{children}</Text>;
};

export const SectionHeading = ({children, headingRef}: ChildrenProps & { headingRef?: SectionHeadingRef }) => {
  const {isSm, styles} = usePromotionsText();

  return <Text style={isSm ? styles.title_s : styles.title_l} ref={headingRef}>{children}</Text>;
};

export const Paragraph = ({children, withBottomMargin = true}: ChildrenProps & { withBottomMargin?: boolean }) => {
  const {isSm, styles} = usePromotionsText();

  return (
    <Text style={[...styles[isSm ? 'text_s' : 'text_l'], !withBottomMargin && MainStyle.m_b_0]}>
      {children}
    </Text>
  );
};

export const ListItem = (props: ChildrenProps & { marker: string, withBottomMargin?: boolean }) => {
  const {marker, children, withBottomMargin = false} = props;
  const {isSm, styles} = usePromotionsText();

  return (
    <View style={[MainStyle.row, withBottomMargin && (isSm ? MainStyle.m_b_15 : MainStyle.m_b_30)]}>
      <Text style={isSm ? styles.dot_s : styles.dot_l}>{marker}</Text>
      <Text style={[...styles[isSm ? 'text_s' : 'text_l'], MainStyle.m_b_0]}>{children}</Text>
    </View>
  );
};

export const ListItemGroup = ({marker, children}: ChildrenProps & { marker: string }) => {
  const {isSm, styles} = usePromotionsText();

  return (
    <View style={[MainStyle.row, isSm ? MainStyle.m_b_15 : MainStyle.m_b_30]}>
      <Text style={isSm ? styles.dot_s : styles.dot_l}>{marker}</Text>
      <View style={MainStyle.flex1}>{children}</View>
    </View>
  );
};
